import React, { useEffect, useMemo, useState } from "react";
import FiltroMultiple from "../../../Postventa/Dashboard/Modales/FiltroMultiple";
import { soloFecha, estiloEstatus } from "../../Visitas/visitasprogramadas/datosVisitas";

const ESTADO_VACIO_FILTROS = { asesor: [], cliente: [], tipoVisita: [], linea: [], estatus: [] };
const ESTADO_VACIO_MOSTRAR = { asesor: { open: false }, cliente: { open: false }, tipoVisita: { open: false }, linea: { open: false }, estatus: { open: false } };

// Tabla agrupada por asesor — colapsable: click en la fila de asesor despliega sus clientes
// visitados. Mismo patrón de toggle que DashScorecardGerenteAsesorTable (Dashboard/Vendedor).
// abiertos/onToggle viven en el padre para que un click en la gráfica también pueda desplegar
// el asesor correspondiente aquí. Columnas Asesor/Cliente separadas (con filtro cada una), mismo
// patrón de filtros por columna (FiltroMultiple) que VisitasListado.
// "listado" llega plano desde Reportes/ReporteVisitasProgramadas (listado_visitas) — cada fila
// es una visita con id_visita, idcliente, razon_social, idvendedor, vendedor, idsucursal,
// sucursal, tipo_visita, visita, fecha_visita, estatus, estatus_texto, createdate, idlinea,
// linea, comentario; el agrupamiento por asesor se arma aquí.
export const ReporteVisitasTabla = ({ listado: listadoProp, abiertos, onToggle }) => {
  const listado = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);

  useEffect(() => {
    setOptions({
      asesor: [...new Set(listado.map((c) => c.vendedor).filter(Boolean))].map((v) => ({ value: v, label: v })),
      cliente: [...new Set(listado.map((c) => c.razon_social).filter(Boolean))].map((v) => ({ value: v, label: v })),
      tipoVisita: [...new Set(listado.map((c) => c.visita).filter(Boolean))].map((v) => ({ value: v, label: v })),
      linea: [...new Set(listado.map((c) => c.linea).filter(Boolean))].map((v) => ({ value: v, label: v })),
      estatus: [...new Set(listado.map((c) => c.estatus_texto).filter(Boolean))].map((v) => ({ value: v, label: v })),
    });
  }, [listado]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const hayFiltrosActivos = Object.values(filtros).some((arr) => arr.length > 0);

  // Aplica los filtros activos (en orden de prioridad) sobre el listado plano, y agrupa por
  // asesor (idvendedor/vendedor/sucursal) — programadas/realizadas se calculan sobre lo ya
  // filtrado, para que el conteo de cada fila de asesor coincida con lo que se ve debajo
  const asesoresAgrupados = useMemo(() => {
    let data = [...listado];

    prioridadFiltros.forEach((filtro) => {
      if (filtro === "asesor" && filtros.asesor.length > 0) {
        data = data.filter((c) => filtros.asesor.includes(c.vendedor));
      }
      if (filtro === "cliente" && filtros.cliente.length > 0) {
        data = data.filter((c) => filtros.cliente.includes(c.razon_social));
      }
      if (filtro === "tipoVisita" && filtros.tipoVisita.length > 0) {
        data = data.filter((c) => filtros.tipoVisita.includes(c.visita));
      }
      if (filtro === "linea" && filtros.linea.length > 0) {
        data = data.filter((c) => filtros.linea.includes(c.linea));
      }
      if (filtro === "estatus" && filtros.estatus.length > 0) {
        data = data.filter((c) => filtros.estatus.includes(c.estatus_texto));
      }
    });

    const grupos = new Map();
    data.forEach((c) => {
      if (!grupos.has(c.idvendedor)) {
        grupos.set(c.idvendedor, {
          idvendedor: c.idvendedor,
          vendedor: c.vendedor,
          sucursal: c.sucursal,
          clientes: [],
          programadas: 0,
          realizadas: 0,
        });
      }
      const grupo = grupos.get(c.idvendedor);
      grupo.clientes.push(c);
      grupo.programadas += 1;
      if (c.estatus === "R") grupo.realizadas += 1;
    });

    return [...grupos.values()].sort((a, b) => a.vendedor.localeCompare(b.vendedor, "es"));
  }, [listado, filtros, prioridadFiltros]);

  const totalProgramadas = asesoresAgrupados.reduce((acc, a) => acc + a.programadas, 0);
  const totalRealizadas = asesoresAgrupados.reduce((acc, a) => acc + a.realizadas, 0);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0}>
          <thead>
            <tr>
              <th>
                <div>
                  <FiltroMultiple {...filtroProps("asesor", "Asesor")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("cliente", "Cliente")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Fecha de creación</div>
              </th>
              <th>
                <div className="borde-izq">Fecha de visita</div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("tipoVisita", "Tipo de visita")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("linea", "Línea")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("estatus", "Estatus")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Comentarios</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {asesoresAgrupados.map((asesor) => {
              // Con un filtro activo el asesor inicia desplegado (los clientes que no pasan el
              // filtro ya fueron excluidos arriba), pero el usuario puede contraerlo con click
              // igual que sin filtro — por eso se revisa abiertos[idvendedor] primero
              const abierto = abiertos[asesor.idvendedor];
              const expandido = abierto !== undefined ? abierto : hayFiltrosActivos;
              return (
                <React.Fragment key={asesor.idvendedor}>
                  <tr
                    id={`rv-asesor-${asesor.idvendedor}`}
                    className="rv-row-asesor"
                    onClick={() => onToggle(asesor.idvendedor)}
                  >
                    <td className="rv-td-asesor">
                      <span className="rv-flecha">{expandido ? "▼" : "▶"}</span>
                      <strong>{asesor.vendedor}</strong>
                      <span className="rv-td-asesor-sucursal">{asesor.sucursal}</span>
                    </td>
                    <td></td>
                    <td colSpan={1}></td>
                    <td className="rv-td-contador" colSpan={2}>
                      {asesor.programadas} programadas · {asesor.realizadas} realizadas
                    </td>
                    <td colSpan={3}></td>
                  </tr>

                  {expandido && asesor.clientes.map((c) => {
                    const estilo = estiloEstatus(c.estatus);
                    return (
                      <tr key={c.id_visita} className="rv-row-cliente">
                        <td></td>
                        <td className="rv-td-cliente">{c.razon_social}</td>
                        <td>{soloFecha(c.createdate)}</td>
                        <td>{soloFecha(c.fecha_visita)}</td>
                        <td>{c.visita}</td>
                        <td>{c.linea || "—"}</td>
                        <td>
                          <span className="rv-estatus-pill" style={{ background: estilo.bg, color: estilo.fg }}>
                            {c.estatus_texto}
                          </span>
                        </td>
                        <td className="rv-td-comentario">{c.comentario || "—"}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="crmcs-cot-tabla-total">
        <span>Total ({asesoresAgrupados.length} asesores)</span>
        <span>{totalProgramadas} programadas · {totalRealizadas} realizadas</span>
      </div>
    </div>
  );
};
